import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../../../app/store';
import { SessionStatus } from '../sessionBoundary/sessionBoundary.constants';
import { IssueReportRecord } from './issueReport.props';

const selectIssueReport = (state: RootState) => state.issueReport;

/** Reports in the order they were filed. */
const selectIssueReports = createSelector(
  selectIssueReport,
  (state): IssueReportRecord[] =>
    state.order.map((id) => state.byId[id]).filter(Boolean)
);

/** The record inside the open flag window — what the panel acts on. */
const selectActiveIssueReport = createSelector(
  selectIssueReport,
  (state): IssueReportRecord | null =>
    state.activeReportId ? state.byId[state.activeReportId] ?? null : null
);

const selectActiveFlagWindowExpiresAt = createSelector(
  selectIssueReport,
  (state) => state.activeWindowExpiresAt
);

/** Banked remainder while paused; null while the countdown is running. */
const selectFlagWindowRemainingMs = createSelector(
  selectIssueReport,
  (state) => state.remainingMs
);

/**
 * Reports that still need something from the agent, NEWEST FIRST.
 *
 * The one question the issue-reporting UI asks, and therefore the one selector. The
 * badge counts these, the Issue-button popover lists them and the task-router lobby
 * card lists them; every surface is answering "what have I not finished?".
 *
 * ## Why this replaced five session-scoped selectors
 *
 * HITL-1501 scoped the button badge to the CURRENT SESSION — "how many have I filed
 * for the car in front of me" — which needed the frozen `envelopeIdentity.session_id`
 * matched against a live resolver, plus a separate `sessionStatus === start` check,
 * because after a car departs both `endedSessionId` and `taskAssignedSessionId` still
 * resolve to the session that just ended. Five selectors and a subtle liveness rule
 * existed to answer that question.
 *
 * HITL-1574 asks a different question, and it makes all of that dead weight:
 * `selectSessionIssueReports`, `selectSessionIssueReportCount`,
 * `selectSessionIssueReportsNewestFirst`, `selectPriorSessionIssueReports` and
 * `selectIsSessionLive` are deleted. `resolveIssueReportSessionId` STAYS — the
 * envelope is still built with it; it is only no longer used to filter for display.
 *
 * ## It deliberately reads NO session state
 *
 * Shift-scoped, not session-scoped, and that is the property the lobby depends on
 * entirely: on the task-router lobby every rung of `resolveIssueReportSessionId` is
 * empty and `sessionStatus` is not `start`, so anything session-filtered returns
 * nothing there. The backlog has to survive the car leaving — that is the whole point
 * of it — so it is derived from the records alone.
 *
 * Bounded already, with no new state: `MAX_RETAINED_REPORTS` caps the store at 50 and
 * `user/logout` wipes it, which is what makes "shift" the right word.
 *
 * ## The predicate, both clauses
 *
 * `issue_type === null` is the obvious half: nobody classified it.
 *
 * The second half is that an UNCONFIRMED `transcript_turn` report counts as
 * outstanding even though it HAS a type, because that type is a machine's guess the
 * agent never confirmed. `TranscriptMessage` picks between the two ASR values purely
 * from which line was clicked, and `LiveTranscriptCard` always files
 * `Missing Transcription` — the agent chose to flag, not what to flag. So the row
 * opens with its chip pre-selected and the agent confirms or changes it.
 *
 * Confirmation is `type_confirmed`, set by a chip click and never cleared. This clause
 * read `revision === 0` first, and that was a BUG: `reportAmended` bumps the revision
 * for every amend, so a word-click report where the agent typed a detail and pressed
 * `Later` amended to revision 1 and silently left this list — it had been classified by
 * nobody, and the surface that existed to bring it back no longer showed it. The
 * revision says *something happened*; only `type_confirmed` says *a human chose the
 * type*.
 *
 * Confirming even an UNCHANGED chip still amends, which costs nothing and buys
 * something on the wire: a later envelope carrying a type identical to revision 0, with
 * `classified_from` set, is precisely "a human confirmed the machine's guess", so
 * machine-picked and agent-confirmed stay distinguishable server-side.
 *
 * `!voided` excludes a retracted report: `Discard` sets it, which is why discarding a
 * row removes it from every surface with no extra code.
 *
 * ## Known regression, named rather than hidden
 *
 * Nothing shows already-CLASSIFIED reports any more — the button's history tooltip and
 * the panel's summary line both went with the selectors above. So an agent can re-file
 * something they already handled with no warning. Accepted: the duplicate is harmless
 * on the wire (consumers dedupe on `(issue_id, revision)` and these are separate
 * reports), and a surface that answers "what is left" is worth more mid-shift than one
 * that answers "what have I done".
 */
const selectOutstandingIssueReports = createSelector(
  selectIssueReports,
  (reports): IssueReportRecord[] =>
    reports
      .filter(
        (report) =>
          !report.voided &&
          (report.issue_type === null ||
            (report.source === 'transcript_turn' && !report.type_confirmed))
      )
      // Newest first. Safe in place — `filter` already produced a fresh array — and
      // memoized here rather than reversed at each call site, where a new array every
      // render would defeat the memoization downstream.
      .reverse()
);

/**
 * Whether a NEW report may be filed right now.
 *
 * `isCarPresent || sessionStatus === 'start'` — read as **is there an active order
 * interaction**, which is not the same question as "is a car at the board".
 *
 * ## What each state means here
 *
 * `sessionStatus` has THREE live values, and the third is the one that makes this
 * predicate work: `''` (no session has ever started on this console), `'start'`, and
 * `'end'` (the car left AND `checkSessionEnd` passed). Both `''` and `'end'` block.
 *
 * `'start'` outlives `car_exit` while an order tail is unsettled — a check was created
 * and the final order is not submitted yet, or the cart is not empty. That state is an
 * agent still working the departed car's order, and a defect they file then is ABOUT
 * that order: it carries a real `session_id` and a real frozen context. So it allows.
 *
 * ## Why not simply `isCarPresent`
 *
 * Because that fails CLOSED in the one direction that matters. `isCarPresent` is set by
 * the `car_enter` frame (and by the task-router hook's local `carEnter` action on
 * task_assigned). A missed or late frame would leave an agent unable to report a defect
 * with a car in front of them — while the session is open and everything else works.
 * Including `sessionStatus` means the gate degrades to "allowed" rather than "blocked"
 * when a signal is lost, which is the right direction for a rule whose only job is
 * suppressing junk.
 *
 * It also costs the TRAILING report: wrong total, wrong readback and unnecessary
 * intervention are all things an agent registers as the car pulls away.
 *
 * ## The accepted hole
 *
 * An agent who never settles an order tail — never presses Finish or Cancel Order —
 * keeps the ability to file indefinitely, because nothing forces `checkSessionEnd` to
 * pass. Known and accepted: that state is an order nobody closed, which is itself worth
 * reporting, and closing the hole would mean new state (a car-exit timestamp) plus a
 * tunable grace window.
 *
 * ## Scope — this gates FILING ONLY
 *
 * Not classifying, not retracting. `commitIssueType` and `voidIssue` must keep working
 * with no car and no session: the outstanding backlog exists precisely to be worked
 * BETWEEN cars, from the Issue button's popover and the task-router lobby. If a fixture
 * with no car ever has to gain one to keep a classification test green, this gate has
 * been applied too widely.
 */
const selectCanFileIssueReport = createSelector(
  // Read off the state directly rather than through `selectIsCarPresent` /
  // `selectSessionStatus`, and NOT because it is shorter. `selectors/message` and
  // `reducers/messagingSlice` import each other, so importing that module here drags
  // this file — which `issueReport.slice` now imports — into that cycle, and
  // `createSelector` then runs with an undefined input selector at module init. The
  // failure mode is the thunk throwing on every press.
  //
  // `resolveIssueReportSessionId` reads `state.sessionBoundary` / `state.taskRouter`
  // the same way, for the same reason. What must not be duplicated is the RULE, and
  // that lives once, below.
  (state: RootState) => state.messages?.isCarPresent,
  (state: RootState) => state.sessionBoundary?.sessionStatus,
  (isCarPresent, sessionStatus): boolean =>
    Boolean(isCarPresent) || sessionStatus === SessionStatus.start
);

export {
  selectActiveFlagWindowExpiresAt,
  selectActiveIssueReport,
  selectCanFileIssueReport,
  selectIssueReport,
  selectIssueReports,
  selectFlagWindowRemainingMs,
  selectOutstandingIssueReports,
};
